import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://ethiorealestates.com';
const OG_FN_URL = 'https://omwuihwgfpewephqawdh.supabase.co/functions/v1/og-image';

export type SocialImage =
  | string
  | {
      type: 'property' | 'project' | 'plot' | 'agency';
      id: string;
      title?: string;
      subtitle?: string;
      badge?: string;
    }
  | {
      title: string;
      subtitle?: string;
      badge?: string;
      image?: string;
    };

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  /** Static URL OR a config object that builds a dynamic OG image */
  ogImage?: SocialImage;
  ogType?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  noindex?: boolean;
}

function buildSocialImageUrl(img?: SocialImage): string {
  if (!img) {
    return `${OG_FN_URL}?title=Ethio%20Real%20Estates&subtitle=Find%20your%20next%20home%20in%20Ethiopia`;
  }
  if (typeof img === 'string') {
    return img;
  }
  const params = new URLSearchParams();
  if ('type' in img && img.type && img.id) {
    params.set('type', img.type);
    params.set('id', img.id);
  }
  if ('title' in img && img.title) params.set('title', img.title);
  if ('subtitle' in img && img.subtitle) params.set('subtitle', img.subtitle);
  if ('badge' in img && img.badge) params.set('badge', img.badge);
  if ('image' in img && img.image) params.set('image', img.image);
  return `${OG_FN_URL}?${params.toString()}`;
}

export function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  jsonLd,
  noindex = false,
}: SEOHeadProps) {
  const fullTitle = title.includes('Ethio Real Estates') ? title : `${title} | Ethio Real Estates`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
  const imageUrl = buildSocialImageUrl(ogImage);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* RSS feed discovery (clean first-party URL, proxied to edge function) */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title="Ethio Real Estates Blog"
        href={`${BASE_URL}/rss.xml`}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content="Ethio Real Estates" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
